import {
  Badge,
  Body,
  Box,
  Center,
  Divider,
  Sheet,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from '@hover/blueprint';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@hover/blueprint/chakra';
import React, { useEffect, useState } from 'react';

import type { BasicJobInfoFragment, Job } from 'src/shared/graphql/generated';
import {
  JobImageViewpoints,
  JobModelStates,
} from 'src/shared/graphql/generated';
import {
  hideScrollbars,
  hideScrollbarsStyles,
} from 'src/shared/helpers/styles';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { SHEET_DEFAULT_PADDING } from '../../constants/sheet';
import { MiniSplitCard } from '../MiniSplitCard';
import { useGetScans } from '../Projects/Project/hooks';
import { ScanDetails } from '../Scan/ScanDetails';

const HiddenScroll = hideScrollbars(Box);
const MAX_IMAGES = 3;
const backgroundSecondary = '#F7F5F5';

const MyScans = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { scans } = useGetScans();
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [exteriorScanOpen, setExteriorScanOpen] = useState(true);
  const [selectedScan, setSelectedScan] = useState<Job | undefined>(undefined);

  const [modalClosed, setModalClosed] = useState(false);

  const handleOnClose = () => {
    onClose();
    setModalClosed(true);
  };

  // Render the modal again when scanDetails is closed
  // this updates the scans list after a scan is deleted
  useEffect(() => {
    if (modalClosed) setModalClosed(false);
  }, [modalClosed]);

  if (scans.length === 0)
    return <Divider borderColor="neutral.200" margin="0" size="small" />;

  const getEyebrow = (scan: Job) => {
    if (scan.bestFrontImage?.viewpoint === JobImageViewpoints.Interior)
      return t('profileSheet.interiorScan');
    return t('profileSheet.exteriorScan');
  };

  const getImages = (images: Job['images']) => {
    const scanImages = images
      .slice(0, MAX_IMAGES)
      .map(image => image.imageUrl!);

    return scanImages.length ? scanImages : undefined;
  };

  const getBadgeStatus = (state?: JobModelStates | null) => {
    if (
      state === JobModelStates.Complete ||
      state === JobModelStates.Uploading ||
      state === null
    )
      return undefined;
    return state;
  };

  const getBadgeColorScheme = (state?: JobModelStates | null) => {
    // TODO: We need to know what color we want for each state
    switch (state) {
      case JobModelStates.Cancelled:
      case JobModelStates.Failed:
      case JobModelStates.Paying:
        return 'danger';
      case JobModelStates.Processing:
      case JobModelStates.ProcessingUpload:
      case JobModelStates.ProcessingUploadForImprovements:
      case JobModelStates.RequestingCorrections:
      case JobModelStates.RequestingImprovements:
      case JobModelStates.Retrieving:
      case JobModelStates.Submitting:
      case JobModelStates.Uploading:
      case JobModelStates.WaitingApproval:
        return 'warning';
      case JobModelStates.Complete:
      default:
        return 'success';
    }
  };

  const handleChange = () => setExteriorScanOpen(!exteriorScanOpen);

  const handleClick = (scan: Job) => {
    setSelectedScan(scan);
    onOpen();
  };

  const renderScans = () => (
    <HiddenScroll
      justifyContent={{ tablet: 'center' }}
      maxHeight={700}
      maxWidth="100%"
      overflowX={{ base: 'scroll', tablet: 'hidden' }}
      overflowY={{ tablet: 'scroll' }}
      pb={800}
      scrollSnapType={{ base: 'x mandatory', tablet: 'y mandatory' }}
    >
      <Box
        flexDirection={{ base: 'row', tablet: 'column' }}
        gap={200}
        height={{ tablet: 'fit-content' }}
      >
        {scans.map(scan => (
          // TODO: we need to fix the shadow issue on desktop, we need to make the sheet component wider in order to
          // show the shadow correctly
          // By other hand, we need to handle how to overlap the horizontal padding for mobile in order to show
          // the card until the limit of the screen
          <MiniSplitCard
            badgeColorScheme={getBadgeColorScheme(scan.state)}
            badgeStatus={getBadgeStatus(scan.state)}
            eyebrow={getEyebrow(scan)}
            headline={scan.name!}
            images={getImages(scan.images)}
            key={scan.id}
            leftBackground={isMobile ? backgroundSecondary : 'primary.100'}
            onClick={() => handleClick(scan)}
            rightBackground="neutral.0"
            shadow="default"
            subCopy={scan.locationLine1!}
          />
        ))}
      </Box>
    </HiddenScroll>
  );

  return (
    <>
      <VStack pt={500} width="100%">
        <Box
          display={{ base: 'none', tablet: 'flex' }}
          flexDirection="column"
          gap={200}
          px={SHEET_DEFAULT_PADDING}
          width="100%"
        >
          <Body fontWeight={700}>{t('profileSheet.scans')}</Body>
          <Divider borderColor="neutral.200" margin="0" size="small" />
        </Box>
        <Box
          display={{ base: 'flex', tablet: 'none' }}
          gap={200}
          px={SHEET_DEFAULT_PADDING}
          width="100%"
        >
          <Body fontWeight={700}>{t('profileSheet.myScans')}</Body>
          <Body>{t('profileSheet.scansCount', { count: scans.length })}</Body>
        </Box>
        {isMobile ? (
          <>{renderScans()}</>
        ) : (
          <>
            <Accordion
              allowToggle
              defaultIndex={[0]}
              onChange={handleChange}
              width="100%"
            >
              <AccordionItem>
                <AccordionButton
                  _hover={{ background: 'none' }}
                  background="none"
                  border="none"
                  borderRadius="5px"
                  color="neutral.900"
                  cursor="pointer"
                  display="flex"
                  justifyContent="space-between"
                  px={SHEET_DEFAULT_PADDING}
                >
                  <Body fontWeight={700} textStyle="body300">
                    {t('profileSheet.exterior')}
                  </Body>
                  <Center>
                    <Badge
                      backgroundColor="black"
                      display={exteriorScanOpen ? 'none' : 'flex'}
                    >
                      {scans.length}
                    </Badge>
                    <AccordionIcon />
                  </Center>
                </AccordionButton>
                <AccordionPanel pt={300}>{renderScans()}</AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Box
              mb={600}
              mt="0 !important"
              px={SHEET_DEFAULT_PADDING}
              width="100%"
            >
              <Divider borderColor="neutral.200" size="small" />
            </Box>
          </>
        )}
      </VStack>
      <Sheet
        contentProps={{
          width: { base: '100%', tablet: 'auto' },
          maxWidth: { base: '100%', tablet: '888px' },
          paddingRight: '0 !important',
          paddingLeft: '0 !important',
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
          height: '100%',
          overflowY: 'auto',
          sx: { ...hideScrollbarsStyles(), borderRadius: 0 },
        }}
        isClosable={false}
        isOpen={isOpen}
        onClose={handleOnClose}
        scrollBehavior="inside"
        size={{ base: 'full', tablet: 'auto' }}
        variant="modal"
      >
        <ScanDetails
          onClose={handleOnClose}
          scan={selectedScan as BasicJobInfoFragment}
        />
      </Sheet>
    </>
  );
};

export { MyScans };
